import { z } from "zod";
import { webAgentMetaData } from "../agents-metadata/web/web-agent-metadata";
import { sqlAgentMetaData } from "../agents-metadata/sql/sql-agent-metadata";
import { virustotalAgentMetaData } from "../agents-metadata/virustotal/virustotal-agent-metadata";
// import { notionAgentMetaData } from "../agents-metadata/notion/notion-agent-metadata";
import { jiraAgentMetaData } from "../agents-metadata/jira/jira-agent-metadata";
import { confluenceAgentMetaData } from "../agents-metadata/confluence/confluence-agent-metadata";
import { mediawikiAgentMetaData } from "../agents-metadata/mediawiki/mediawiki-agent-metadata";
import { phpipamAgentMetaData } from "../agents-metadata/phpipam/phpipam-agent-metadata";
// import { filesAgentMetaData } from "../agents-metadata/files/files-agent-metadata";
import { apiAgentMetaData } from "../agents-metadata/api/api-agent-metadata";
// import { dalleAgentMetaData } from "../agents-metadata/dalle/dalle-agent-metadata";
// import { websitesAgentMetaData } from "../agents-metadata/websites/websites-agent-metadata";
import { routerAgentMetaData } from "../agents-metadata/router/router-agent-metadata";
import { synthesizerAgentMetaData } from "../agents-metadata/synthesizer/synthesizer-agent-metadata";

/**
 * Agent Type Configuration Registry
 * This is the SINGLE SOURCE OF TRUTH for all agent types.
 * To add a new agent type, simply add it to this configuration object.
 */

export const essentialAgents = ["router", "synthesizer", "visualizer"];

// Individual agent type configurations
export const agentRegistry = {
  router: routerAgentMetaData,
  // visualizer: visualizerAgentMetaData,
  synthesizer: synthesizerAgentMetaData,
  confluence: confluenceAgentMetaData,
  jira: jiraAgentMetaData,
  mediawiki: mediawikiAgentMetaData,
  phpipam: phpipamAgentMetaData,
  // notion: notionAgentMetaData,
  virustotal: virustotalAgentMetaData,
  sql: sqlAgentMetaData,
  web: webAgentMetaData,
  api: apiAgentMetaData,
  // dalle: dalleAgentMetaData,
  // websites: websitesAgentMetaData,
  // files: filesAgentMetaData,
} as const;

// Dynamically generate types and schemas from the configuration
export type AgentTypeKey = keyof typeof agentRegistry;
export const agentTypeKeys = Object.keys(agentRegistry) as AgentTypeKey[];

// Generate agent type enum schema dynamically
export const agentTypeSchema = z.enum(
  agentTypeKeys as [AgentTypeKey, ...AgentTypeKey[]]
);

// Generate config schemas dynamically with proper typing
export const agentConfigSchemas = {
  router: agentRegistry.router.configSchema,
  // visualizer: agentRegistry.visualizer.configSchema,
  synthesizer: agentRegistry.synthesizer.configSchema,
  confluence: agentRegistry.confluence.configSchema,
  jira: agentRegistry.jira.configSchema,
  mediawiki: agentRegistry.mediawiki.configSchema,
  phpipam: agentRegistry.phpipam.configSchema,
  // notion: agentRegistry.notion.configSchema,
  virustotal: agentRegistry.virustotal.configSchema,
  sql: agentRegistry.sql.configSchema,
  web: agentRegistry.web.configSchema,
  api: agentRegistry.api.configSchema,
  // dalle: agentRegistry.dalle.configSchema,
  // websites: agentRegistry.websites.configSchema,
  // files: agentRegistry.files.configSchema,
} as const satisfies {
  [K in AgentTypeKey]: (typeof agentRegistry)[K]["configSchema"];
};

// Generate union type for all configs
const configSchemaValues = Object.values(agentConfigSchemas);
export const agentConnectionConfigSchema = z.union(
  configSchemaValues as unknown as [
    z.ZodTypeAny,
    z.ZodTypeAny,
    ...z.ZodTypeAny[]
  ]
);

// Inferred types
export type AgentConfigMap = {
  [K in AgentTypeKey]: z.infer<(typeof agentRegistry)[K]["configSchema"]>;
};

export type AgentWorkflow = {
  id: string;
  name: string;
  description?: string;
  triggerLabel: string;
};

// Agent type metadata
export interface AgentTypeMetadata<
  TConfigSchema extends z.ZodSchema = z.ZodSchema
> {
  name: string;
  description: string;
  category: string | "Essential" | "Knowledgebase" | "Management";
  icon: string;
  type: string;
  version: string;
  ribbon: string | null;
  status: string | null;
  href: string;
  instructions: string;
  placeholders: Record<string, string>;
  configSchema: TConfigSchema;
  workflows?: AgentWorkflow[];
  sensitiveKeys?: string[];
}

export const agentTypeMetadata = agentRegistry satisfies Record<
  AgentTypeKey,
  AgentTypeMetadata
>;

// Helper functions
export const isValidAgentType = (type: string): type is AgentTypeKey =>
  agentTypeKeys.includes(type as AgentTypeKey);

// Get all agent types grouped by category
export const getAgentTypesByCategory = () => {
  const categories: Record<
    string,
    Array<{ key: AgentTypeKey; metadata: AgentTypeMetadata }>
  > = {};

  for (const [key, metadata] of Object.entries(agentTypeMetadata) as Array<
    [AgentTypeKey, AgentTypeMetadata]
  >) {
    if (!categories[metadata.category]) {
      categories[metadata.category] = [];
    }
    categories[metadata.category].push({ key, metadata });
  }

  return categories;
};

// Validate configuration for a specific agent type
export const validateAgentConfig = (type: AgentTypeKey, config: unknown) => {
  const schema = agentConfigSchemas[type];
  return schema.safeParse(config);
};

/**
 * TO ADD A NEW AGENT TYPE:
 * 1. Add it to the `agentRegistry` object above
 * 2. That's it! Everything else is generated automatically:
 *    - Types are inferred
 *    - Schemas are created
 *    - Validation works
 *    - Service functions work
 *    - API endpoints work
 *
 * Example:
 * slack: {
 *   name: "Slack",
 *   description: "Connect to Slack workspaces and channels",
 *   category: "communication",
 *   icon: "slack",
 *   configSchema: baseAgentConfig.extend({
 *     botToken: z.string().min(1, "Required"),
 *     appToken: z.string().min(1, "Required"),
 *     signingSecret: z.string().min(1, "Required"),
 *   }),
 * },
 */
